/**
 * The five categories a teacher navigates by, in render order.
 *
 * `id` is the server's category key — the same string that names the folder in
 * the content bucket, so a file is classified by where it is filed and nothing
 * has to guess. `solutions` has no folder: it is derived from packed lesson
 * content at request time.
 *
 * Every category renders whether or not the pack has it. Coverage is lopsided
 * (LCX has 403 extension files, HWN has one document in total, BRN has none),
 * and hiding the gaps would imply a uniformity that does not exist.
 */
const CATEGORIES = [
  {
    id: 'solutions',
    label: 'Code Solutions',
    description: 'Per-objective reference code',
    tint: '#4C8DFF',
    icon: 'M9.4 16.6 4.8 12l4.6-4.6L8 6l-6 6 6 6zm5.2 0 4.6-4.6-4.6-4.6L16 6l6 6-6 6z',
  },
  {
    id: 'answer-keys',
    label: 'Answer Keys',
    description: 'Mission logs & AP CSP keys',
    tint: '#E0A33E',
    icon: 'M12.65 10A5.99 5.99 0 0 0 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6a5.99 5.99 0 0 0 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z',
  },
  {
    id: 'assessments',
    label: 'Assessments',
    description: 'Review questions & unit tests',
    tint: '#C55FB0',
    icon: 'M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm-7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm-2 14-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z',
  },
  {
    id: 'guides',
    label: 'Teacher Guides',
    description: 'Teacher editions & curriculum',
    tint: '#3FA37A',
    icon: 'M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-5 2v8l-2.5-1.5L8 12V4h5z',
  },
  {
    id: 'extensions',
    label: 'Extensions',
    description: 'Remixes & cross-curricular',
    tint: '#7C6BD8',
    icon: 'M20 2H4c-1.1 0-2 .9-2 2v3.01c0 .72.43 1.34 1 1.69V20c0 1.1 1.1 2 2 2h14c.9 0 2-.9 2-2V8.7c.57-.35 1-.97 1-1.69V4c0-1.1-.9-2-2-2zm-5 12H9v-2h6v2zm5-7H4V4h16v3z',
  },
]

const DEFAULT_CATEGORY = CATEGORIES[0].id

function categoryById(id) {
  return CATEGORIES.find(c => c.id === id) || CATEGORIES[0]
}

/** Total documents across every category except the derived solutions. */
function documentCount(pack) {
  return CATEGORIES
    .filter(c => c.id !== 'solutions')
    .reduce((sum, c) => sum + (pack?.categories?.[c.id]?.count || 0), 0)
}

function countFor(pack, categoryId) {
  return pack?.categories?.[categoryId]?.count || 0
}

/** The first category this pack actually has, so a switch never lands on empty. */
function firstNonEmptyCategory(pack) {
  const found = CATEGORIES.find(c => countFor(pack, c.id) > 0)
  return found ? found.id : DEFAULT_CATEGORY
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return ''
  }
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0)} kB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export {
  CATEGORIES,
  DEFAULT_CATEGORY,
  categoryById,
  countFor,
  documentCount,
  firstNonEmptyCategory,
  formatBytes,
}
